<script setup>
import { footerLogo, wechatImage, footerColumns, policies } from '@/views/home/components/guestHomeData.js'

// public 文件使用部署 base，避免生产 /ai-open-platform/ 子路径下素材失效。
const asset = name =>
  `${import.meta.env.BASE_URL}image/${name.includes('/') ? name : `home-redesign/${name}`}`
</script>

<template>
  <footer class="guest-footer" aria-labelledby="guest-footer-title">
    <div class="guest-container">
      <h2 id="guest-footer-title" class="sr-only">移远通信官网信息</h2>
      <a class="footer-brand" v-track="'footer > quectel'" href="https://www.quectel.com.cn/" aria-label="移远通信官网"><img :src="asset(footerLogo)" alt="Quectel 移远通信" loading="lazy" width="32" height="22" /></a>
      <div class="footer-columns">
        <section v-for="(column, index) in footerColumns" :key="column.title" :aria-labelledby="'guest-footer-column-' + index">
          <h3 :id="'guest-footer-column-' + index">{{ column.title }}</h3>
          <ul>
            <li v-for="item in column.links" :key="item.name">
              <a v-if="item.link" :href="item.link" :target="item.target" :rel="item.target === '_blank' ? 'noopener noreferrer' : undefined" v-track="{ position: 'footer > link', name: item.name }">{{ item.name }}</a>
              <span v-else class="footer-item-text">{{ item.name }}</span>
            </li>
          </ul>
        </section>
        <section class="footer-contact" aria-labelledby="guest-footer-contact-title">
          <h3 id="guest-footer-contact-title">联系我们</h3>
          <a href="tel:4009607678" v-track="'footer > telephone'">全国热线：400 960 7678</a>
          <address>全球中心：上海市松江区泗泾镇外婆泾路8号</address>
          <p>微信公众号：移远通信</p>
          <img :src="asset(wechatImage)" alt="移远通信微信公众号二维码" loading="lazy" width="120" height="120" />
        </section>
      </div>
      <div class="footer-legal">
        <ul class="footer-policies"><li v-for="item in policies" :key="item.name"><a :href="item.href" v-track="{ position: 'footer > policy', name: item.name }" target="_blank" rel="noopener noreferrer">{{ item.name }}</a></li></ul>
        <div class="footer-filing">
          <a href="https://beian.mps.gov.cn/#/query/webSearch?code=31011702889058" target="_blank" rel="noopener noreferrer">沪公网安备31011702889058</a>
          <a href="https://beian.miit.gov.cn/" target="_blank" rel="noopener noreferrer">沪ICP备18027903号-9</a>
        </div>
      </div>
    </div>
  </footer>
</template>

<style scoped>
/* 补齐原 guest-home 的通用依赖，独立复用时保持同一套设计。 */
.guest-footer { width: 100%; min-width: 0; font-family: Stratos-Regular, 'Source Han Sans', Arial, sans-serif, -apple-system, BlinkMacSystemFont; -webkit-font-smoothing: antialiased; }
.guest-footer, .guest-footer *, .guest-footer *::before, .guest-footer *::after { box-sizing: border-box; }
.guest-footer h2, .guest-footer h3, .guest-footer p, .guest-footer ul { margin: 0; padding: 0; }
.guest-footer ul { list-style: none; }
.guest-footer a { color: inherit; text-decoration: none; }
.guest-footer a:focus-visible { outline: 3px solid #1478d4; outline-offset: 4px; }
.guest-footer img { display: block; max-width: 100%; }
.guest-footer h2 { font-size: 22px; line-height: 1.2; font-weight: 700; }
.guest-container { width: min(1440px, calc(100% - 48px)); margin-inline: auto; }
.sr-only { position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px; overflow: hidden; clip-path: inset(50%); white-space: nowrap; border: 0; }
.guest-footer { padding-block: 48px; color: #fff; background: #0d182b; }
.guest-footer .guest-container { display: flex; flex-direction: column; gap: 32px; }
.footer-brand { align-self: flex-start; }
.footer-columns { display: grid; grid-template-columns: 140fr 176fr 144fr 148fr 132fr 144fr 144fr 144fr 128fr 260fr; gap: 24px; }
.footer-columns > section { min-width: 0; }
.guest-footer h3 { color: #fff; font-size: 16px; line-height: normal; font-weight: 600; margin-bottom: 16px; }
.footer-columns ul, .footer-contact { display: flex; flex-direction: column; gap: 8px; }
.guest-footer a, .guest-footer p, .guest-footer address, .footer-item-text { color: rgb(255 255 255 / 80%); font-size: 13px; font-weight: 500; line-height: 1.25; font-style: normal; }
.guest-footer a:hover { color: #ed000c; text-decoration: underline; }
.footer-contact h3 { margin-bottom: 8px; }
.guest-footer .footer-contact address, .footer-contact a { line-height: normal; }
.guest-footer .footer-contact p { font-size: 12px; }
.footer-contact img { border-radius: 4px; margin-top: 0; }
.footer-legal { border-top: 1px solid rgb(255 255 255 / 16%); padding-top: 24px; display: flex; flex-direction: column; gap: 16px; }
.footer-policies { display: flex; flex-wrap: wrap; gap: 8px; }
.footer-policies li + li::before { content: '|'; color: rgb(255 255 255 / 80%); margin-right: 8px; }
.footer-filing { display: flex; flex-wrap: wrap; gap: 16px; }
.footer-policies a { font-size: 14px; line-height: normal; }
.footer-filing a { font-size: 13px; line-height: normal; }
@media (max-width: 1199px) {
  .footer-columns { grid-template-columns: repeat(4, minmax(0, 1fr)); row-gap: 32px; }
}
@media (max-width: 767px) {
  .guest-container { width: calc(100% - 32px); }
  .guest-footer h2 { overflow-wrap: anywhere; }
  .guest-footer { padding-block: 32px; }
  .footer-columns { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 32px 20px; }
  .footer-contact { overflow-wrap: anywhere; }
}
</style>